/* Â© NXAPP Limited */
import { useState, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthContext } from '@/context/AuthContext'
import { supabase } from '@/lib/supabase'
import { londonDayKey } from '@/lib/trend'
import {
  LayoutDashboard, Users, Layers, Megaphone, FileText,
  Sparkles, Settings, LogOut, User, Mail, Building2, Upload, Menu, X, ShieldOff, BookOpen, Radar, PenLine, Target, Phone,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import UpdateGate, { BUILD_ID } from '@/components/UpdateGate'

// `module` links a nav item to its AI-spend bucket in ai_usage (approx $).
const nav = [
  { to: '/',             label: 'Dashboard',            icon: LayoutDashboard, exact: true },
  { to: '/contacts',     label: 'Subscribed Members',   icon: Users },
  { to: '/suppression',  label: 'Suppression Log',      icon: ShieldOff },
  { to: '/leads',        label: 'Lead Engine (CH)',     icon: Building2, module: 'leads' },
  { to: '/leads-import', label: 'Lead Engine (Import)', icon: Upload },
  { to: '/prospect',     label: 'Auto Prospect',        icon: Target, module: 'auto_prospect' },
  { to: '/segments',     label: 'Segments',             icon: Layers },
  { to: '/campaigns',    label: 'Campaigns',            icon: Megaphone },
  { to: '/tm',           label: 'TM — Telemarketing',   icon: Phone, module: 'tm' },
  { to: '/templates',    label: 'Templates',            icon: FileText },
  { to: '/ai-draft',     label: 'AI Draft',             icon: Sparkles, module: 'ai_draft' },
  { to: '/media',        label: 'Media Rapid-Response', icon: Radar, module: 'media' },
  { to: '/letters',      label: 'Media — Letters',      icon: PenLine, module: 'weekly' },
] as { to: string; label: string; icon: typeof Users; exact?: boolean; module?: string }[]

interface Spend { today: number; week: number; month: number }

const bottomNav = [
  { to: '/guide',    label: 'Guide & Help', icon: BookOpen },
  { to: '/profile',  label: 'Profile',  icon: User },
  { to: '/settings', label: 'Settings', icon: Settings },
]

export default function AdminLayout() {
  const { user, signOut } = useAuthContext()
  const navigate = useNavigate()
  const location = useLocation()

  // Approx AI spend per module (today / this week / this month), from ai_usage.
  const { data: spend } = useQuery({
    queryKey: ['ai-spend', user?.id],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86_400_000).toISOString()
      const { data } = await supabase.from('ai_usage')
        .select('module, cost_usd, created_at').eq('user_id', user!.id).gte('created_at', since).limit(20000)
      const rows = (data ?? []) as { module: string; cost_usd: number; created_at: string }[]
      const now = Date.now(); const todayKey = londonDayKey(new Date())
      const by: Record<string, Spend> = {}
      let total: Spend = { today: 0, week: 0, month: 0 }
      for (const r of rows) {
        const c = Number(r.cost_usd) || 0
        const s = (by[r.module] ??= { today: 0, week: 0, month: 0 })
        const t = new Date(r.created_at).getTime()
        s.month += c; total = { ...total, month: total.month + c }
        if (t >= now - 7 * 86_400_000) { s.week += c; total.week += c }
        if (londonDayKey(new Date(r.created_at)) === todayKey) { s.today += c; total.today += c }
      }
      return { by, total }
    },
  })
  const money = (n: number) => n >= 10 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`
  // Sidebar is a fixed off-canvas drawer below lg, static from lg up.
  const [navOpen, setNavOpen] = useState(false)
  // Close the drawer on navigation, otherwise it covers the page you just opened.
  useEffect(() => { setNavOpen(false) }, [location.pathname])

  async function handleSignOut() {
    await signOut()
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile top bar — only below lg */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 flex items-center gap-3 px-4 h-14 bg-white border-b border-gray-200">
        <button
          onClick={() => setNavOpen(true)}
          className="p-2 -ml-2 text-gray-600 hover:text-gray-900 rounded-lg"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="w-7 h-7 rounded-lg bg-brand-600 flex items-center justify-center shrink-0">
          <Mail className="w-3.5 h-3.5 text-white" />
        </div>
        <span className="font-semibold text-gray-900 text-sm truncate">NXAPP Bulletin</span>
      </div>

      {/* Backdrop behind the open drawer */}
      {navOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/40"
          onClick={() => setNavOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        'w-60 flex flex-col bg-white border-r border-gray-200 shrink-0',
        // Drawer below lg; static column from lg up.
        'fixed inset-y-0 left-0 z-50 transition-transform duration-200 lg:static lg:translate-x-0 lg:z-auto',
        navOpen ? 'translate-x-0' : '-translate-x-full',
      )}>
        {/* Logo */}
        <div className="flex items-center gap-2 px-5 py-5 border-b border-gray-100">
          <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center">
            <Mail className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold text-gray-900">NXAPP Bulletin</span>
          <button
            onClick={() => setNavOpen(false)}
            className="lg:hidden ml-auto p-1 text-gray-400 hover:text-gray-700 rounded"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Main nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {nav.map(({ to, label, icon: Icon, exact, module }) => {
            const s = module ? spend?.by?.[module] : undefined
            return (
            <NavLink
              key={to}
              to={to}
              end={exact}
              className={({ isActive }) => cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
              )}
            >
              <Icon className="w-4 h-4 shrink-0" />
              <span className="truncate">{label}</span>
              {module && (s?.month ?? 0) > 0 && (
                <span
                  className="ml-auto shrink-0 text-[10px] tabular-nums text-gray-400"
                  title={`Approx AI spend — today ${money(s!.today)} · this week ${money(s!.week)} · this month ${money(s!.month)}`}
                >
                  {money(s!.today)}
                </span>
              )}
            </NavLink>
            )
          })}
        </nav>

        {/* Bottom nav */}
        <div className="px-3 py-3 border-t border-gray-100 space-y-0.5">
          {bottomNav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
              )}
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          ))}
          {(spend?.total?.month ?? 0) > 0 && (
            <div className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-100" title="Approx Anthropic AI spend across all modules">
              <p className="text-[10px] uppercase tracking-wide text-gray-400">AI spend (approx)</p>
              <p className="text-[11px] text-gray-600 tabular-nums">
                today <b className="text-gray-800">{money(spend!.total.today)}</b>
                <span className="mx-1 text-gray-300">·</span>wk <b className="text-gray-800">{money(spend!.total.week)}</b>
                <span className="mx-1 text-gray-300">·</span>mo <b className="text-gray-800">{money(spend!.total.month)}</b>
              </p>
            </div>
          )}
          <div className="px-3 py-2 text-xs text-gray-400 truncate">
            {user?.email}
            <span className="block text-[10px] text-gray-300">build {BUILD_ID}</span>
          </div>
          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-y-auto pt-14 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <Outlet />
        </div>
      </main>

      <UpdateGate />
    </div>
  )
}

